import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Download,
  TrendingUp,
  Users,
  MapPin,
  AlertCircle,
  Eye,
  EyeOff,
  Globe,
  Calendar,
  BarChart3,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [visitorStats, setVisitorStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || '1234';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      fetchData();
    } else {
      alert('Mot de passe incorrect');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const adminToken = process.env.REACT_APP_ADMIN_TOKEN || 'change-this-secret-token-123';
      const headers = { Authorization: `Bearer ${adminToken}` };

      const [statsResp, leadsResp, visitorResp] = await Promise.all([
        fetch('/api/stats', { headers }),
        fetch('/api/leads/all', { headers }),
        fetch('/api/stats/visitors', { headers }),
      ]);

      if (statsResp.ok) setStats(await statsResp.json());
      if (leadsResp.ok) {
        const data = await leadsResp.json();
        setLeads(data.leads || []);
      }
      if (visitorResp.ok) setVisitorStats(await visitorResp.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const adminToken = process.env.REACT_APP_ADMIN_TOKEN || 'change-this-secret-token-123';
      const response = await fetch('/api/leads/export', {
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('CSV téléchargé avec succès!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert("Erreur lors de l'export CSV");
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Card className="p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold mb-2">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground">A Life Elsewhere</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="admin-password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="admin-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Entrez le mot de passe admin"
                    className="pr-10"
                    required
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" data-testid="admin-login-submit">
                Se connecter
              </Button>

              <div className="mt-4 p-3 bg-muted rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    Mot de passe par défaut: <code className="bg-background px-1 rounded">1234</code>
                    <br />
                    <span className="text-primary">Changez-le dans le code!</span>
                  </p>
                </div>
              </div>
            </form>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Flag emoji from 2-letter country code
  const flagFromCode = (code) => {
    if (!code || code.length !== 2 || code === 'XX') return '🌍';
    const base = 0x1f1e6;
    const A = 'A'.charCodeAt(0);
    return String.fromCodePoint(
      base + code.toUpperCase().charCodeAt(0) - A,
      base + code.toUpperCase().charCodeAt(1) - A
    );
  };

  // Prepare daily chart data (last 30 days, fill gaps with 0)
  const buildDailyChartData = () => {
    if (!visitorStats?.daily_visits) return [];
    const map = {};
    visitorStats.daily_visits.forEach((d) => {
      map[d.date] = d;
    });
    const result = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = d.toISOString().split('T')[0];
      const entry = map[key] || { visits: 0, unique_visitors: 0 };
      result.push({
        date: key.slice(5), // MM-DD
        visits: entry.visits || 0,
        unique: entry.unique_visitors || 0,
      });
    }
    return result;
  };

  const chartData = buildDailyChartData();
  const maxCountryCount = visitorStats?.by_country?.[0]?.count || 1;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold">Dashboard Admin</h1>
              <p className="text-muted-foreground mt-1">
                A Life Elsewhere — Gestion des leads & visiteurs
              </p>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleExportCSV} className="gap-2" data-testid="export-csv-button">
                <Download className="w-4 h-4" />
                Exporter CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setAuthenticated(false);
                  setPassword('');
                }}
                data-testid="logout-button"
              >
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Chargement...</p>
          </div>
        ) : (
          <Tabs defaultValue="leads" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2 mb-8">
              <TabsTrigger value="leads" data-testid="tab-leads">
                <Users className="w-4 h-4 mr-2" />
                Leads
              </TabsTrigger>
              <TabsTrigger value="visitors" data-testid="tab-visitors">
                <BarChart3 className="w-4 h-4 mr-2" />
                Visiteurs
              </TabsTrigger>
            </TabsList>

            {/* ==== LEADS TAB ==== */}
            <TabsContent value="leads" className="space-y-8">
              {stats && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card className="p-6" data-testid="kpi-total-leads">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Users className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Leads</p>
                        <p className="text-3xl font-bold">{stats.total_leads}</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6" data-testid="kpi-recent-leads">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-accent/30 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">7 Derniers Jours</p>
                        <p className="text-3xl font-bold">{stats.recent_leads_7_days}</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6" data-testid="kpi-regions">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Régions</p>
                        <p className="text-3xl font-bold">
                          {stats.leads_by_region?.length || 0}
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold">📋 Tous les leads</h2>
                  <Badge variant="outline">{leads.length} total</Badge>
                </div>

                {leads.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    Aucun lead pour le moment
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-sm">Email</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">WhatsApp</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">Region</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">Gender</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">Age</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((lead) => (
                          <tr
                            key={lead.email}
                            className="border-b hover:bg-muted/50 transition-colors"
                          >
                            <td className="py-3 px-4 text-sm">{lead.email}</td>
                            <td className="py-3 px-4 text-sm font-mono">{lead.whatsapp}</td>
                            <td className="py-3 px-4 text-sm">{lead.region || '-'}</td>
                            <td className="py-3 px-4 text-sm">{lead.gender || '-'}</td>
                            <td className="py-3 px-4 text-sm">{lead.age || '-'}</td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {lead.created_at
                                ? new Date(lead.created_at).toLocaleDateString('fr-FR')
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* ==== VISITORS TAB ==== */}
            <TabsContent value="visitors" className="space-y-8">
              {!visitorStats ? (
                <Card className="p-12 text-center text-muted-foreground">
                  Aucune donnée de visite disponible.
                </Card>
              ) : (
                <>
                  {/* KPI Cards */}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card className="p-6" data-testid="kpi-total-visits">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <BarChart3 className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Visites</p>
                          <p className="text-3xl font-bold">{visitorStats.total_visits}</p>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-6" data-testid="kpi-visits-today">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-accent/30 flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Aujourd'hui</p>
                          <p className="text-3xl font-bold">{visitorStats.visits_today}</p>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-6" data-testid="kpi-visits-7d">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">7 Derniers Jours</p>
                          <p className="text-3xl font-bold">{visitorStats.visits_7_days}</p>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-6" data-testid="kpi-unique-visitors">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Users className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Uniques (30j)</p>
                          <p className="text-3xl font-bold">{visitorStats.unique_visitors_30d}</p>
                        </div>
                      </div>
                    </Card>
                  </div>

                  {/* Daily Chart */}
                  <Card className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-xl font-bold">📈 Visites quotidiennes</h2>
                        <p className="text-sm text-muted-foreground">30 derniers jours</p>
                      </div>
                    </div>
                    <div className="w-full h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                            interval={4}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                            allowDecimals={false}
                          />
                          <Tooltip
                            contentStyle={{
                              background: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              fontSize: '13px',
                            }}
                            labelStyle={{ color: 'hsl(var(--foreground))' }}
                          />
                          <Bar dataKey="visits" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>

                  {/* Country Breakdown */}
                  <Card className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-primary" />
                        <div>
                          <h2 className="text-xl font-bold">🌍 Visiteurs par pays</h2>
                          <p className="text-sm text-muted-foreground">Toutes périodes confondues</p>
                        </div>
                      </div>
                      <Badge variant="outline">
                        {visitorStats.by_country?.length || 0} pays
                      </Badge>
                    </div>

                    {(!visitorStats.by_country || visitorStats.by_country.length === 0) ? (
                      <p className="text-muted-foreground text-center py-8">
                        Aucune donnée pays pour le moment.
                      </p>
                    ) : (
                      <div className="space-y-3" data-testid="country-list">
                        {visitorStats.by_country.map((c, i) => {
                          const pct = Math.round((c.count / maxCountryCount) * 100);
                          return (
                            <div
                              key={`${c.country_code}-${i}`}
                              className="flex items-center gap-4"
                              data-testid={`country-row-${c.country_code}`}
                            >
                              <div className="flex items-center gap-3 w-48 flex-shrink-0">
                                <span className="text-2xl" aria-hidden="true">
                                  {flagFromCode(c.country_code)}
                                </span>
                                <span className="text-sm font-medium truncate">{c.country}</span>
                              </div>
                              <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-500"
                                  style={{ width: `${Math.max(pct, 3)}%` }}
                                />
                              </div>
                              <span className="text-sm font-mono w-16 text-right">{c.count}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>

                  {/* Daily Visits Table */}
                  <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold">🗓️ Détail par jour</h2>
                      <Badge variant="outline">{chartData.length} jours</Badge>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 font-semibold text-sm">Date</th>
                            <th className="text-right py-3 px-4 font-semibold text-sm">Visites</th>
                            <th className="text-right py-3 px-4 font-semibold text-sm">
                              Visiteurs uniques
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...chartData].reverse().map((d) => (
                            <tr
                              key={d.date}
                              className="border-b hover:bg-muted/50 transition-colors"
                            >
                              <td className="py-3 px-4 text-sm font-mono">
                                {new Date().getFullYear()}-{d.date}
                              </td>
                              <td className="py-3 px-4 text-sm text-right font-semibold">
                                {d.visits}
                              </td>
                              <td className="py-3 px-4 text-sm text-right text-muted-foreground">
                                {d.unique}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
